import { useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../config";

const SESSION_KEY = "trp_session_id";

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

function track(event, page, data = {}) {
  try {
    navigator.sendBeacon?.(
      `${API}/analytics/track`,
      new Blob([JSON.stringify({ session_id: getSessionId(), event, page, data })], { type: "application/json" })
    ) || axios.post(`${API}/analytics/track`, { session_id: getSessionId(), event, page, data }).catch(() => {});
  } catch (e) {}
}

// Public helper for explicit semantic events (e.g. funnel CTA clicks).
// Generic clicks are auto-captured below, but named events give us clean
// metrics for revenue-impacting actions instead of dredging through every
// click target string in the admin panel.
export function trackEvent(event, data = {}) {
  const page = typeof window !== "undefined"
    ? window.location.pathname + window.location.hash
    : "";
  track(event, page, data);
}

export function useBehaviorTracker() {
  const pageStart = useRef(Date.now());
  const lastPage = useRef("");
  const scrollMax = useRef(0);
  const scrollTimer = useRef(null);

  const trackPageView = useCallback(() => {
    const page = window.location.pathname + window.location.hash;
    if (page === lastPage.current) return;

    // Send time_on_page for previous page
    if (lastPage.current) {
      const seconds = Math.round((Date.now() - pageStart.current) / 1000);
      if (seconds > 1 && seconds < 1800) {
        track("time_on_page", lastPage.current, { seconds });
      }
      // Send max scroll depth for previous page
      if (scrollMax.current > 5) {
        track("scroll", lastPage.current, { depth: scrollMax.current });
      }
    }

    lastPage.current = page;
    pageStart.current = Date.now();
    scrollMax.current = 0;
    track("page_view", page);
  }, []);

  useEffect(() => {
    trackPageView();

    // Scroll tracking (throttled)
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const depth = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      if (depth > scrollMax.current) scrollMax.current = depth;
    };

    // Click tracking
    const onClick = (e) => {
      const el = e.target.closest("[data-testid], a, button");
      if (!el) return;
      const target = el.dataset?.testid || el.textContent?.slice(0, 40)?.trim() || el.tagName;
      const page = window.location.pathname + window.location.hash;
      track("click", page, { target, tag: el.tagName, href: el.href || "" });
    };

    // Exit tracking
    const onBeforeUnload = () => {
      const page = window.location.pathname + window.location.hash;
      const seconds = Math.round((Date.now() - pageStart.current) / 1000);
      track("exit", page, { seconds, scroll_depth: scrollMax.current });
      if (seconds > 1) track("time_on_page", page, { seconds });
      if (scrollMax.current > 5) track("scroll", page, { depth: scrollMax.current });
    };

    // Visibility change (tab switch = potential exit)
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        const page = window.location.pathname + window.location.hash;
        track("tab_hidden", page, { scroll_depth: scrollMax.current });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick, { capture: true });
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);

    // URL change detection (SPA routing)
    const interval = setInterval(() => {
      const current = window.location.pathname + window.location.hash;
      if (current !== lastPage.current) trackPageView();
    }, 1000);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [trackPageView]);
}
